// Country/timezone -> currency resolution for the Plus paywall (Option A:
// manual local-currency pricing). Amounts are NOT stored here — Stripe is the
// source of truth via `currency_options` on the Plus Prices; this file only
// resolves WHICH currency a user should be charged in, and prefills the
// selector from the browser timezone.
//
// Keys in `countryToCurrency` are ISO 3166-1 alpha-2 codes matching the values
// of `countriesToCodes` in `~/utils/countries.ts`. Any country not listed here
// (and any unknown input) falls back to USD.

export const DEFAULT_CURRENCY = "usd";

// The currencies we actively support with local `currency_options` in Stripe.
// A resolved currency NOT in this set must fall back to USD (see
// `resolveCurrency`), so we never request a currency Stripe has no amount for.
export const SUPPORTED_CURRENCIES = [
  "usd",
  "gbp",
  "eur",
  "cad",
  "aud",
  "nzd",
  "chf",
  "sek",
  "nok",
  "dkk",
  "pln",
  "ron",
  "czk",
  "huf",
  "brl",
  "mxn",
  "jpy",
  "sgd",
  "hkd",
  "thb",
  "myr",
  "idr",
  "php",
  "inr",
  "zar",
  "try",
  "aed",
  "sar",
  "vnd",
  // Added from top-50 userbase analysis (Mixpanel). All clean 2-decimal
  // except krw/clp (zero-decimal, already in ZERO_DECIMAL_CURRENCIES).
  "krw",
  "clp",
  "dzd",
  "cop",
  "bsd",
  "twd",
  "mad",
  "pen",
  "gel",
  "uah",
  "mmk",
  "egp",
] as const;

export type SupportedCurrency = (typeof SUPPORTED_CURRENCIES)[number];

// Currencies with no minor unit (whole-number amounts). Mirrors the set used in
// `formatPrice.ts`; kept here for validation of Stripe minor-unit amounts.
export const ZERO_DECIMAL_CURRENCIES = new Set(["vnd", "jpy", "krw", "clp"]);

// ISO alpha-2 country -> currency. Covers every country in countriesToCodes.
// Eurozone countries all map to "eur"; countries that officially use USD map to
// "usd". Markets without a dedicated local Price fall back to USD at resolve
// time even if listed, so listing is safe.
export const countryToCurrency: Record<string, string> = {
  US: "usd",
  // --- Eurozone ---
  AT: "eur", BE: "eur", CY: "eur", HR: "eur", EE: "eur", FI: "eur",
  FR: "eur", DE: "eur", GR: "eur", IE: "eur", IT: "eur", LV: "eur",
  LT: "eur", LU: "eur", MT: "eur", MC: "eur", NL: "eur", PT: "eur",
  SK: "eur", SI: "eur", ES: "eur", SM: "eur", ME: "eur", AD: "eur",
  BG: "eur", // joined the eurozone 1 Jan 2026
  // --- Rest of Europe (non-euro) ---
  GB: "gbp", CH: "chf", LI: "chf", SE: "sek", NO: "nok", DK: "dkk",
  IS: "isk", PL: "pln", RO: "ron", CZ: "czk", HU: "huf",
  RS: "rsd", MK: "mkd", AL: "all", MD: "mdl", TR: "try", AM: "amd",
  AZ: "azn", KZ: "kzt", BA: "bam", GE: "gel", UA: "uah",
  // --- Americas ---
  CA: "cad", MX: "mxn", BR: "brl", AR: "ars", CL: "clp", CO: "cop",
  PE: "pen", UY: "uyu", PY: "pyg", BO: "bob", GT: "gtq", CR: "crc",
  DO: "dop", JM: "jmd", TT: "ttd", BS: "bsd", GY: "gyd", LC: "xcd",
  AG: "xcd", PA: "usd", EC: "usd", SV: "usd",
  // --- Middle East & Africa ---
  AE: "aed", SA: "sar", OM: "omr", EG: "egp", MA: "mad",
  TN: "tnd", DZ: "dzd", IQ: "iqd", ZA: "zar", NG: "ngn", GH: "ghs", KE: "kes",
  ET: "etb", RW: "rwf", NA: "nad", BW: "bwp", MU: "mur", MZ: "mzn",
  MG: "mga", AO: "aoa", BJ: "xof", CI: "xof", SN: "xof", NE: "xof",
  // --- Asia-Pacific ---
  AU: "aud", NZ: "nzd", JP: "jpy", KR: "krw", CN: "cny", HK: "hkd",
  MO: "mop", TW: "twd", SG: "sgd", MY: "myr", ID: "idr", PH: "php",
  TH: "thb", VN: "vnd", IN: "inr", PK: "pkr", BD: "bdt", LK: "lkr",
  KH: "khr", LA: "lak", MN: "mnt", BT: "inr", MM: "mmk",
};

// IANA timezone -> ISO alpha-2 country, used to pick the default country (and
// thus presentment currency) from Intl.DateTimeFormat().resolvedOptions().timeZone.
// This is geography only; it knows nothing about Stripe. Whether the country
// gets a local price is decided later by resolveCurrency (unsupported => USD).
// Generated from the IANA tz database (zone.tab) so every canonical timezone is
// covered, plus legacy aliases (e.g. Asia/Calcutta) that older browsers still
// report. Timezones with no country (UTC, Etc/*) are intentionally absent and
// fall back to US/USD in detectCountryFromTimezone.
export const timezoneToCountry: Record<string, string> = {
  "Africa/Abidjan": "CI", "Africa/Accra": "GH", "Africa/Addis_Ababa": "ET",
  "Africa/Algiers": "DZ", "Africa/Asmara": "ER", "Africa/Bamako": "ML",
  "Africa/Bangui": "CF", "Africa/Banjul": "GM", "Africa/Bissau": "GW",
  "Africa/Blantyre": "MW", "Africa/Brazzaville": "CG", "Africa/Bujumbura": "BI",
  "Africa/Cairo": "EG", "Africa/Casablanca": "MA", "Africa/Ceuta": "ES",
  "Africa/Conakry": "GN", "Africa/Dakar": "SN", "Africa/Dar_es_Salaam": "TZ",
  "Africa/Djibouti": "DJ", "Africa/Douala": "CM", "Africa/El_Aaiun": "EH",
  "Africa/Freetown": "SL", "Africa/Gaborone": "BW", "Africa/Harare": "ZW",
  "Africa/Johannesburg": "ZA", "Africa/Juba": "SS", "Africa/Kampala": "UG",
  "Africa/Khartoum": "SD", "Africa/Kigali": "RW", "Africa/Kinshasa": "CD",
  "Africa/Lagos": "NG", "Africa/Libreville": "GA", "Africa/Lome": "TG",
  "Africa/Luanda": "AO", "Africa/Lubumbashi": "CD", "Africa/Lusaka": "ZM",
  "Africa/Malabo": "GQ", "Africa/Maputo": "MZ", "Africa/Maseru": "LS",
  "Africa/Mbabane": "SZ", "Africa/Mogadishu": "SO", "Africa/Monrovia": "LR",
  "Africa/Nairobi": "KE", "Africa/Ndjamena": "TD", "Africa/Niamey": "NE",
  "Africa/Nouakchott": "MR", "Africa/Ouagadougou": "BF", "Africa/Porto-Novo": "BJ",
  "Africa/Sao_Tome": "ST", "Africa/Tripoli": "LY", "Africa/Tunis": "TN",
  "Africa/Windhoek": "NA", "America/Adak": "US", "America/Anchorage": "US",
  "America/Anguilla": "AI", "America/Antigua": "AG", "America/Araguaina": "BR",
  "America/Argentina/Buenos_Aires": "AR", "America/Argentina/Catamarca": "AR",
  "America/Argentina/Cordoba": "AR", "America/Argentina/Jujuy": "AR",
  "America/Argentina/La_Rioja": "AR", "America/Argentina/Mendoza": "AR",
  "America/Argentina/Rio_Gallegos": "AR", "America/Argentina/Salta": "AR",
  "America/Argentina/San_Juan": "AR", "America/Argentina/San_Luis": "AR",
  "America/Argentina/Tucuman": "AR", "America/Argentina/Ushuaia": "AR",
  "America/Aruba": "AW", "America/Asuncion": "PY", "America/Atikokan": "CA",
  "America/Bahia": "BR", "America/Bahia_Banderas": "MX", "America/Barbados": "BB",
  "America/Belem": "BR", "America/Belize": "BZ", "America/Blanc-Sablon": "CA",
  "America/Boa_Vista": "BR", "America/Bogota": "CO", "America/Boise": "US",
  "America/Cambridge_Bay": "CA", "America/Campo_Grande": "BR", "America/Cancun": "MX",
  "America/Caracas": "VE", "America/Cayenne": "GF", "America/Cayman": "KY",
  "America/Chicago": "US", "America/Chihuahua": "MX", "America/Ciudad_Juarez": "MX",
  "America/Costa_Rica": "CR", "America/Coyhaique": "CL", "America/Creston": "CA",
  "America/Cuiaba": "BR", "America/Curacao": "CW", "America/Danmarkshavn": "GL",
  "America/Dawson": "CA", "America/Dawson_Creek": "CA", "America/Denver": "US",
  "America/Detroit": "US", "America/Dominica": "DM", "America/Edmonton": "CA",
  "America/Eirunepe": "BR", "America/El_Salvador": "SV", "America/Fort_Nelson": "CA",
  "America/Fortaleza": "BR", "America/Glace_Bay": "CA", "America/Goose_Bay": "CA",
  "America/Grand_Turk": "TC", "America/Grenada": "GD", "America/Guadeloupe": "GP",
  "America/Guatemala": "GT", "America/Guayaquil": "EC", "America/Guyana": "GY",
  "America/Halifax": "CA", "America/Havana": "CU", "America/Hermosillo": "MX",
  "America/Indiana/Indianapolis": "US", "America/Indiana/Knox": "US",
  "America/Indiana/Marengo": "US", "America/Indiana/Petersburg": "US",
  "America/Indiana/Tell_City": "US", "America/Indiana/Vevay": "US",
  "America/Indiana/Vincennes": "US", "America/Indiana/Winamac": "US",
  "America/Inuvik": "CA", "America/Iqaluit": "CA", "America/Jamaica": "JM",
  "America/Juneau": "US", "America/Kentucky/Louisville": "US",
  "America/Kentucky/Monticello": "US", "America/Kralendijk": "BQ", "America/La_Paz": "BO",
  "America/Lima": "PE", "America/Los_Angeles": "US", "America/Lower_Princes": "SX",
  "America/Maceio": "BR", "America/Managua": "NI", "America/Manaus": "BR",
  "America/Marigot": "MF", "America/Martinique": "MQ", "America/Matamoros": "MX",
  "America/Mazatlan": "MX", "America/Menominee": "US", "America/Merida": "MX",
  "America/Metlakatla": "US", "America/Mexico_City": "MX", "America/Miquelon": "PM",
  "America/Moncton": "CA", "America/Monterrey": "MX", "America/Montevideo": "UY",
  "America/Montserrat": "MS", "America/Nassau": "BS", "America/New_York": "US",
  "America/Nome": "US", "America/Noronha": "BR", "America/North_Dakota/Beulah": "US",
  "America/North_Dakota/Center": "US", "America/North_Dakota/New_Salem": "US",
  "America/Nuuk": "GL", "America/Ojinaga": "MX", "America/Panama": "PA",
  "America/Paramaribo": "SR", "America/Phoenix": "US", "America/Port-au-Prince": "HT",
  "America/Port_of_Spain": "TT", "America/Porto_Velho": "BR", "America/Puerto_Rico": "PR",
  "America/Punta_Arenas": "CL", "America/Rankin_Inlet": "CA", "America/Recife": "BR",
  "America/Regina": "CA", "America/Resolute": "CA", "America/Rio_Branco": "BR",
  "America/Santarem": "BR", "America/Santiago": "CL", "America/Santo_Domingo": "DO",
  "America/Sao_Paulo": "BR", "America/Scoresbysund": "GL", "America/Sitka": "US",
  "America/St_Barthelemy": "BL", "America/St_Johns": "CA", "America/St_Kitts": "KN",
  "America/St_Lucia": "LC", "America/St_Thomas": "VI", "America/St_Vincent": "VC",
  "America/Swift_Current": "CA", "America/Tegucigalpa": "HN", "America/Thule": "GL",
  "America/Tijuana": "MX", "America/Toronto": "CA", "America/Tortola": "VG",
  "America/Vancouver": "CA", "America/Whitehorse": "CA", "America/Winnipeg": "CA",
  "America/Yakutat": "US", "Antarctica/Casey": "AQ", "Antarctica/Davis": "AQ",
  "Antarctica/DumontDUrville": "AQ", "Antarctica/Macquarie": "AU",
  "Antarctica/Mawson": "AQ", "Antarctica/McMurdo": "AQ", "Antarctica/Palmer": "AQ",
  "Antarctica/Rothera": "AQ", "Antarctica/Syowa": "AQ", "Antarctica/Troll": "AQ",
  "Antarctica/Vostok": "AQ", "Arctic/Longyearbyen": "SJ", "Asia/Aden": "YE",
  "Asia/Almaty": "KZ", "Asia/Amman": "JO", "Asia/Anadyr": "RU", "Asia/Aqtau": "KZ",
  "Asia/Aqtobe": "KZ", "Asia/Ashgabat": "TM", "Asia/Atyrau": "KZ", "Asia/Baghdad": "IQ",
  "Asia/Bahrain": "BH", "Asia/Baku": "AZ", "Asia/Bangkok": "TH", "Asia/Barnaul": "RU",
  "Asia/Beirut": "LB", "Asia/Bishkek": "KG", "Asia/Brunei": "BN", "Asia/Chita": "RU",
  "Asia/Colombo": "LK", "Asia/Damascus": "SY", "Asia/Dhaka": "BD", "Asia/Dili": "TL",
  "Asia/Dubai": "AE", "Asia/Dushanbe": "TJ", "Asia/Famagusta": "CY", "Asia/Gaza": "PS",
  "Asia/Hebron": "PS", "Asia/Ho_Chi_Minh": "VN", "Asia/Hong_Kong": "HK", "Asia/Hovd": "MN",
  "Asia/Irkutsk": "RU", "Asia/Jakarta": "ID", "Asia/Jayapura": "ID",
  "Asia/Jerusalem": "IL", "Asia/Kabul": "AF", "Asia/Kamchatka": "RU", "Asia/Karachi": "PK",
  "Asia/Kathmandu": "NP", "Asia/Khandyga": "RU", "Asia/Kolkata": "IN",
  "Asia/Krasnoyarsk": "RU", "Asia/Kuala_Lumpur": "MY", "Asia/Kuching": "MY",
  "Asia/Kuwait": "KW", "Asia/Macau": "MO", "Asia/Magadan": "RU", "Asia/Makassar": "ID",
  "Asia/Manila": "PH", "Asia/Muscat": "OM", "Asia/Nicosia": "CY",
  "Asia/Novokuznetsk": "RU", "Asia/Novosibirsk": "RU", "Asia/Omsk": "RU",
  "Asia/Oral": "KZ", "Asia/Phnom_Penh": "KH", "Asia/Pontianak": "ID",
  "Asia/Pyongyang": "KP", "Asia/Qatar": "QA", "Asia/Qostanay": "KZ",
  "Asia/Qyzylorda": "KZ", "Asia/Riyadh": "SA", "Asia/Sakhalin": "RU",
  "Asia/Samarkand": "UZ", "Asia/Seoul": "KR", "Asia/Shanghai": "CN",
  "Asia/Singapore": "SG", "Asia/Srednekolymsk": "RU", "Asia/Taipei": "TW",
  "Asia/Tashkent": "UZ", "Asia/Tbilisi": "GE", "Asia/Tehran": "IR", "Asia/Thimphu": "BT",
  "Asia/Tokyo": "JP", "Asia/Tomsk": "RU", "Asia/Ulaanbaatar": "MN", "Asia/Urumqi": "CN",
  "Asia/Ust-Nera": "RU", "Asia/Vientiane": "LA", "Asia/Vladivostok": "RU",
  "Asia/Yakutsk": "RU", "Asia/Yangon": "MM", "Asia/Yekaterinburg": "RU",
  "Asia/Yerevan": "AM", "Atlantic/Azores": "PT", "Atlantic/Bermuda": "BM",
  "Atlantic/Canary": "ES", "Atlantic/Cape_Verde": "CV", "Atlantic/Faroe": "FO",
  "Atlantic/Madeira": "PT", "Atlantic/Reykjavik": "IS", "Atlantic/South_Georgia": "GS",
  "Atlantic/St_Helena": "SH", "Atlantic/Stanley": "FK", "Australia/Adelaide": "AU",
  "Australia/Brisbane": "AU", "Australia/Broken_Hill": "AU", "Australia/Darwin": "AU",
  "Australia/Eucla": "AU", "Australia/Hobart": "AU", "Australia/Lindeman": "AU",
  "Australia/Lord_Howe": "AU", "Australia/Melbourne": "AU", "Australia/Perth": "AU",
  "Australia/Sydney": "AU", "Europe/Amsterdam": "NL", "Europe/Andorra": "AD",
  "Europe/Astrakhan": "RU", "Europe/Athens": "GR", "Europe/Belgrade": "RS",
  "Europe/Berlin": "DE", "Europe/Bratislava": "SK", "Europe/Brussels": "BE",
  "Europe/Bucharest": "RO", "Europe/Budapest": "HU", "Europe/Busingen": "DE",
  "Europe/Chisinau": "MD", "Europe/Copenhagen": "DK", "Europe/Dublin": "IE",
  "Europe/Gibraltar": "GI", "Europe/Guernsey": "GG", "Europe/Helsinki": "FI",
  "Europe/Isle_of_Man": "IM", "Europe/Istanbul": "TR", "Europe/Jersey": "JE",
  "Europe/Kaliningrad": "RU", "Europe/Kirov": "RU", "Europe/Kyiv": "UA",
  "Europe/Lisbon": "PT", "Europe/Ljubljana": "SI", "Europe/London": "GB",
  "Europe/Luxembourg": "LU", "Europe/Madrid": "ES", "Europe/Malta": "MT",
  "Europe/Mariehamn": "AX", "Europe/Minsk": "BY", "Europe/Monaco": "MC",
  "Europe/Moscow": "RU", "Europe/Oslo": "NO", "Europe/Paris": "FR",
  "Europe/Podgorica": "ME", "Europe/Prague": "CZ", "Europe/Riga": "LV",
  "Europe/Rome": "IT", "Europe/Samara": "RU", "Europe/San_Marino": "SM",
  "Europe/Sarajevo": "BA", "Europe/Saratov": "RU", "Europe/Simferopol": "UA",
  "Europe/Skopje": "MK", "Europe/Sofia": "BG", "Europe/Stockholm": "SE",
  "Europe/Tallinn": "EE", "Europe/Tirane": "AL", "Europe/Ulyanovsk": "RU",
  "Europe/Vaduz": "LI", "Europe/Vatican": "VA", "Europe/Vienna": "AT",
  "Europe/Vilnius": "LT", "Europe/Volgograd": "RU", "Europe/Warsaw": "PL",
  "Europe/Zagreb": "HR", "Europe/Zurich": "CH", "Indian/Antananarivo": "MG",
  "Indian/Chagos": "IO", "Indian/Christmas": "CX", "Indian/Cocos": "CC",
  "Indian/Comoro": "KM", "Indian/Kerguelen": "TF", "Indian/Mahe": "SC",
  "Indian/Maldives": "MV", "Indian/Mauritius": "MU", "Indian/Mayotte": "YT",
  "Indian/Reunion": "RE", "Pacific/Apia": "WS", "Pacific/Auckland": "NZ",
  "Pacific/Bougainville": "PG", "Pacific/Chatham": "NZ", "Pacific/Chuuk": "FM",
  "Pacific/Easter": "CL", "Pacific/Efate": "VU", "Pacific/Fakaofo": "TK",
  "Pacific/Fiji": "FJ", "Pacific/Funafuti": "TV", "Pacific/Galapagos": "EC",
  "Pacific/Gambier": "PF", "Pacific/Guadalcanal": "SB", "Pacific/Guam": "GU",
  "Pacific/Honolulu": "US", "Pacific/Kanton": "KI", "Pacific/Kiritimati": "KI",
  "Pacific/Kosrae": "FM", "Pacific/Kwajalein": "MH", "Pacific/Majuro": "MH",
  "Pacific/Marquesas": "PF", "Pacific/Midway": "UM", "Pacific/Nauru": "NR",
  "Pacific/Niue": "NU", "Pacific/Norfolk": "NF", "Pacific/Noumea": "NC",
  "Pacific/Pago_Pago": "AS", "Pacific/Palau": "PW", "Pacific/Pitcairn": "PN",
  "Pacific/Pohnpei": "FM", "Pacific/Port_Moresby": "PG", "Pacific/Rarotonga": "CK",
  "Pacific/Saipan": "MP", "Pacific/Tahiti": "PF", "Pacific/Tarawa": "KI",
  "Pacific/Tongatapu": "TO", "Pacific/Wake": "UM", "Pacific/Wallis": "WF",
  // Legacy / alias names
  "Africa/Asmera": "KE", "Africa/Timbuktu": "CI", "America/Argentina/ComodRivadavia": "AR",
  "America/Atka": "US", "America/Buenos_Aires": "AR", "America/Catamarca": "AR",
  "America/Coral_Harbour": "PA", "America/Cordoba": "AR", "America/Ensenada": "MX",
  "America/Fort_Wayne": "US", "America/Godthab": "GL", "America/Indianapolis": "US",
  "America/Jujuy": "AR", "America/Knox_IN": "US", "America/Louisville": "US",
  "America/Mendoza": "AR", "America/Montreal": "CA", "America/Nipigon": "CA",
  "America/Pangnirtung": "CA", "America/Porto_Acre": "BR", "America/Rainy_River": "CA",
  "America/Rosario": "AR", "America/Santa_Isabel": "MX", "America/Shiprock": "US",
  "America/Thunder_Bay": "CA", "America/Virgin": "PR", "America/Yellowknife": "CA",
  "Antarctica/South_Pole": "NZ", "Asia/Ashkhabad": "TM", "Asia/Calcutta": "IN",
  "Asia/Choibalsan": "MN", "Asia/Chongqing": "CN", "Asia/Chungking": "CN",
  "Asia/Dacca": "BD", "Asia/Harbin": "CN", "Asia/Istanbul": "TR", "Asia/Kashgar": "CN",
  "Asia/Katmandu": "NP", "Asia/Macao": "MO", "Asia/Rangoon": "MM", "Asia/Saigon": "VN",
  "Asia/Tel_Aviv": "IL", "Asia/Thimbu": "BT", "Asia/Ujung_Pandang": "ID",
  "Asia/Ulan_Bator": "MN", "Atlantic/Faeroe": "FO", "Atlantic/Jan_Mayen": "DE",
  "Australia/ACT": "AU", "Australia/Canberra": "AU", "Australia/Currie": "AU",
  "Australia/LHI": "AU", "Australia/NSW": "AU", "Australia/North": "AU",
  "Australia/Queensland": "AU", "Australia/South": "AU", "Australia/Tasmania": "AU",
  "Australia/Victoria": "AU", "Australia/West": "AU", "Australia/Yancowinna": "AU",
  "Brazil/Acre": "BR", "Brazil/DeNoronha": "BR", "Brazil/East": "BR", "Brazil/West": "BR",
  "CET": "BE", "CST6CDT": "US", "Canada/Atlantic": "CA", "Canada/Central": "CA",
  "Canada/Eastern": "CA", "Canada/Mountain": "CA", "Canada/Newfoundland": "CA",
  "Canada/Pacific": "CA", "Canada/Saskatchewan": "CA", "Canada/Yukon": "CA",
  "Chile/Continental": "CL", "Chile/EasterIsland": "CL", "Cuba": "CU", "EET": "GR",
  "EST": "PA", "EST5EDT": "US", "Egypt": "EG", "Eire": "IE", "Europe/Belfast": "GB",
  "Europe/Kiev": "UA", "Europe/Nicosia": "CY", "Europe/Tiraspol": "MD",
  "Europe/Uzhgorod": "UA", "Europe/Zaporozhye": "UA", "GB": "GB", "GB-Eire": "GB",
  "HST": "US", "Hongkong": "HK", "Iceland": "CI", "Iran": "IR", "Israel": "IL",
  "Jamaica": "JM", "Japan": "JP", "Kwajalein": "MH", "Libya": "LY", "MET": "BE",
  "MST": "US", "MST7MDT": "US", "Mexico/BajaNorte": "MX", "Mexico/BajaSur": "MX",
  "Mexico/General": "MX", "NZ": "NZ", "NZ-CHAT": "NZ", "Navajo": "US", "PRC": "CN",
  "PST8PDT": "US", "Pacific/Enderbury": "KI", "Pacific/Johnston": "US",
  "Pacific/Ponape": "SB", "Pacific/Samoa": "AS", "Pacific/Truk": "PG", "Pacific/Yap": "PG",
  "Poland": "PL", "Portugal": "PT", "ROC": "TW", "ROK": "KR", "Singapore": "SG",
  "Turkey": "TR", "US/Alaska": "US", "US/Aleutian": "US", "US/Arizona": "US",
  "US/Central": "US", "US/East-Indiana": "US", "US/Eastern": "US", "US/Hawaii": "US",
  "US/Indiana-Starke": "US", "US/Michigan": "US", "US/Mountain": "US", "US/Pacific": "US",
  "US/Samoa": "AS", "W-SU": "RU", "WET": "PT",
};

// Resolve a country code to a supported currency, falling back to USD if the
// country is unknown or maps to a currency we don't yet price locally.
export function resolveCurrency(countryIso: string | null | undefined): string {
  if (!countryIso) return DEFAULT_CURRENCY;
  const cur = countryToCurrency[countryIso.toUpperCase()];
  if (cur && (SUPPORTED_CURRENCIES as readonly string[]).includes(cur)) {
    return cur;
  }
  return DEFAULT_CURRENCY;
}

// Detect the buyer's country from the browser timezone.
export function detectCountryFromTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timezoneToCountry[tz] ?? "US";
  } catch {
    return "US";
  }
}
